import * as THREE from 'three';
import gsap from 'gsap';
// Note: You will need to have a GameMover.js file for this to work.
// A placeholder is provided below.
import GameMover from './GameMover.js';
import Stats from 'stats.js';


class CrossyRoad {
    // --- Heavily expanded configuration for game balancing and AR/VR ---
    config = {
        // --- NEW: AR/VR Configuration ---
        ar: {
            startPosition: { x: 0, y: -0.5, z: -0.5 },
            moverStartPosition: new THREE.Vector3(0, 7, -5),
            moverStartScale: new THREE.Vector3(7, 0.8, 0.2), // Scale for the game mover in AR
        },
        vr: {
            scale: 0.05, // Makes the game feel like a tabletop model
            controllerDeadZone: 0.2, // For analog stick
            controllerThreshold: 0.7, // How far to push the stick to trigger a move
        },
        ui: {
            buttonSize: 1.0,
            iconPaths: {
                play: 'assets/icons/play.png',
                restart: 'assets/icons/restart.png',
                passthroughShow: 'assets/icons/passthrough_show.png',
                passthroughHide: 'assets/icons/passthrough_hide.png',
                sfxOn: 'assets/icons/sfx_on.png',
                sfxOff: 'assets/icons/sfx_off.png',
            }
        },
        // --- Original Game Configuration ---
        camera: {
            position: new THREE.Vector3(0, 10, 10),
            lookAt: new THREE.Vector3(0, 0, 0),
        },
        world: {
            laneWidth: 12,
            laneCount: 20,
            laneChunks: {
                grass: { min: 2, max: 5 },
                road: { min: 2, max: 6 },
                water: { min: 2, max: 4 },
            }
        },
        player: {
            size: 0.8,
            hopHeight: 0.5,
            hopDuration: 0.2,
        },
        // ... (rest of the original config is unchanged) ...
        coin: {
            spawnChance: 0.3,
            geometry: {
                radius: 0.3,
                height: 0.1,
            },
            value: 10,
            rotationSpeed: 2.5,
        },
        obstacle: {
            grass: {
                obstacleCount: { min: 1, max: 3 }
            },
            road: {
                initialDensity: 0.4,
                minSpacing: 2,
                traffic: {
                    spawnCooldown: { min: 3, max: 8 }
                },
                carTypes: {
                    width: 0.9,
                    mainHeight: 0.7,
                    wheel: {
                        radius: 0.22,
                        width: 0.2,
                        color: 0x111111,
                    },
                    headlight: {
                        size: 0.15,
                        color: 0xffff00
                    },
                    roofColor: 0xffffff,
                    small: { length: 2 },
                    big: { length: 3 },
                    truck: {
                        length: 4.5,
                        cab: {
                            length: 1.5,
                            height: 1.2,
                        },
                        trailer: {
                            length: 2.8,
                            height: 1.5,
                        }
                    },
                    speed: 3
                }
            },
            log: {
                initialDensity: 0.5,
                minSpacing: 1,
                traffic: {
                    spawnCooldown: { min: 2, max: 5 }
                },
                logType: {
                    length: { min: 2, max: 4 },
                    width: 0.9,
                    height: 0.4,
                    speed: 2
                }
            },
            tree: {
                trunkRadius: 0.2,
                trunkHeight: { min: 0.8, max: 1.5 },
                crownRadius: 0.6,
            },
            bush: {
                radius: 0.5,
                color: 0x4a7d2c,
            }
        },
        train: {
            spawnCooldown: { min: 8, max: 15 },
            warningTime: 2.5,
            speed: 15,
            visuals: {
                carCount: { min: 4, max: 8 },
                spacing: 0.2,
                wheel: {
                    radius: 0.5,
                    width: 0.3,
                    color: 0x111111,
                },
                locomotive: {
                    length: 2,
                    height: 1.5,
                    width: 1.0,
                    cabHeight: 0.8,
                    color: 0x3d3d3d,
                    headlight: {
                        size: 0.3,
                        color: 0xffff00
                    }
                },
                car: {
                    length: 3.5,
                    height: 1.5,
                    width: 1.0,
                    color: 0x8f8f8f,
                },
                cargo: {
                    size: { min: 0.8, max: 0.8 },
                    height: { min: 0.1, max: 0.1 },
                    count: { min: 1, max: 2 }
                }
            }
        }
    };

    // --- Game State ---
    gameEnded;
    lanes = [];
    cars = [];
    trains = [];
    logs = [];
    coins = [];
    player;
    playerGridPos = { x: 0, z: 0 };
    score = 0;
    maxZ = 0;

    currentChunk = { type: 'road', remaining: 0 };
    lastWaterDirection = 1;

    // --- ThreeJS Globals ---
    camera;
    scene;
    renderer;
    gameContainer;
    lastTime = 0;

    // --- NEW: Clipping Plane Properties ---
    localLeftPlane;   // The "blueprint" plane in local space
    localRightPlane;  // The "blueprint" plane in local space
    worldLeftPlane = new THREE.Plane();  // The plane to be updated and used by materials
    worldRightPlane = new THREE.Plane(); // The plane to be updated and used by materials
    clippingPlanes = []; // This array will hold references to the world planes
    leftPlaneHelper;  // Visualizer for the left plane
    rightPlaneHelper; // Visualizer for the right plane

    desktopLight;
    vrLight;

    // --- Input State for Mobile ---
    touchStartPos = { x: 0, y: 0 };

    // --- NEW: XR & 3D UI Properties ---
    gameGroup; // Group for all game objects
    uiGroup; // Group for 3D UI elements
    score3d; // Holds the 3D score object
    controllers = [];
    raycaster;
    mainActionButton;
    passthroughButton;
    sfxButton;
    isPassthroughActive = false;
    isSfxMuted = false;
    textureLoader = new THREE.TextureLoader();
    audioListener;
    sounds = {}; // To hold loaded sounds
    gameMover;

    stats;
    drawCallsPanel;




    constructor({ gameContainer, ui2d, leaderboard }) {
        this.gameContainer = gameContainer;
        this.leaderboard = leaderboard || null;
        this.ui2d = {
            container: ui2d,
            score: ui2d.querySelector('#score-2d'),
            start: ui2d.querySelector('#start-btn-2d'),
            restart: ui2d.querySelector('#restart-btn-2d'),
        };
        this.init();
        this.onResize();
    }

    init = () => {
        this.sceneWidth = window.innerWidth;
        this.sceneHeight = window.innerHeight;

        this.camera = new THREE.PerspectiveCamera(75, this.sceneWidth / this.sceneHeight, 0.1, 1000);
        // Set a default desktop position. It will be overridden in VR.
        this.camera.position.copy(this.config.camera.position);
        this.camera.lookAt(this.config.camera.lookAt);

        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x7ec0ee);

        // --- KEY CHANGE: PARENTING STRUCTURE ---
        this.gameGroup = new THREE.Group();
        this.scene.add(this.gameGroup);

        // NEW: worldGroup will hold all dynamic game elements.
        this.worldGroup = new THREE.Group();
        this.gameGroup.add(this.worldGroup);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
        this.scene.add(ambientLight); // Ambient light can stay in the main scene.

        // --- SETUP LIGHT 1: The Desktop Light ---
        this.desktopLight = new THREE.DirectionalLight(0xffffff, 0.8);
        this.desktopLight.castShadow = true;
        this.desktopLight.shadow.mapSize.width = 2048;
        this.desktopLight.shadow.mapSize.height = 2048;

        // Large frustum for full desktop coverage
        const desktopShadowSize = 20;
        this.desktopLight.shadow.camera.top = desktopShadowSize;
        this.desktopLight.shadow.camera.bottom = -desktopShadowSize;
        this.desktopLight.shadow.camera.left = -desktopShadowSize;
        this.desktopLight.shadow.camera.right = desktopShadowSize;

        // ADD TO SCENE, NOT GAMEGROUP
        this.scene.add(this.desktopLight);
        this.scene.add(this.desktopLight.target);

        // --- SETUP LIGHT 2: The VR Light ---
        this.vrLight = new THREE.DirectionalLight(0xffffff, 0.8);
        this.vrLight.castShadow = true;
        this.vrLight.shadow.mapSize.width = 2048; // Same high resolution...
        this.vrLight.shadow.mapSize.height = 2048;

        // ...but a VERY TIGHT frustum for crisp VR shadows.
        // This size is in the light's LOCAL space, before gameGroup scaling.
        const vrShadowSize = 4;
        this.vrLight.shadow.camera.top = vrShadowSize;
        this.vrLight.shadow.camera.bottom = -vrShadowSize;
        this.vrLight.shadow.camera.left = -vrShadowSize;
        this.vrLight.shadow.camera.right = vrShadowSize;

        this.vrLight.visible = false; // Initially invisible

        this.scene.add(this.vrLight);
        this.scene.add(this.vrLight.target);
        // --- END OF LIGHTING CHANGE ---

        this.clippingPlanes = [];


        this.renderer = new THREE.WebGLRenderer({
            antialias: true, // <-- Explicitly set to false
            alpha: true,
            powerPreference: "high-performance" // <-- This is the key hint to the browser
        });
        this.renderer.localClippingEnabled = true;
        this.renderer.setSize(this.sceneWidth, this.sceneHeight);
        this.renderer.setAnimationLoop(this.animation);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setClearColor(0x000000, 0);

        this.renderer.xr.enabled = true;
        this.gameContainer.appendChild(this.renderer.domElement);
        this.renderer.shadowMap.type = THREE.PCFShadowMap;

        this.bindControls();
        this.setupXrControls();
        this.create3dUi();
        this.create3dScore();

        this.gameMover = new GameMover({
            scene: this.scene,
            gameGroup: this.gameGroup,
            controllers: this.controllers,
            camera: this.camera,
            moveSensitivity: 1,
            moverStartPosition: this.config.ar.moverStartPosition,
            moverStartScale: this.config.ar.moverStartScale
        });

        this.initializeClippingPlanes();
        //this.createClippingPlaneVisualizers(); // For debugging




        this.restart();
        window.addEventListener('resize', this.onResize);
    }

  

    initializeClippingPlanes() {
        const halfLaneWidth = this.config.world.laneWidth / 2;

        // 1. Define planes in LOCAL space, relative to the gameGroup's origin (0,0,0).
        // The normal points "inward" to keep what's inside.
        // Vector(1,0,0) is a normal pointing right, from the left side.
        // Vector(-1,0,0) is a normal pointing left, from the right side.
        this.localLeftPlane = new THREE.Plane(new THREE.Vector3(1, 0, 0), halfLaneWidth);
        this.localRightPlane = new THREE.Plane(new THREE.Vector3(-1, 0, 0), halfLaneWidth);

        // 2. The main clippingPlanes array used by materials will reference our world planes.
        // These will be updated every frame.
        this.clippingPlanes = [this.worldLeftPlane, this.worldRightPlane];
    }

    createClippingPlaneVisualizers() {
        // The size (25) is arbitrary, just make it large enough to see
        this.leftPlaneHelper = new THREE.PlaneHelper(this.worldLeftPlane, 25, 0xff0000); // Red
        this.rightPlaneHelper = new THREE.PlaneHelper(this.worldRightPlane, 25, 0x0000ff); // Blue

        // Add the helpers to the main scene, NOT the gameGroup, because they
        // visualize the planes which exist in world space.
        this.scene.add(this.leftPlaneHelper);
        this.scene.add(this.rightPlaneHelper);
    }

    // --- NEW: XR Session Management ---
    onEnterXR = () => {
        this.ui2d.container.style.display = 'none';

        const { x, y, z } = this.config.ar.startPosition;
        this.gameGroup.position.set(x, y, z);

        const vrScale = this.config.vr.scale;
        this.gameGroup.scale.set(vrScale, vrScale, vrScale);

        const session = this.renderer.xr.getSession();
        const passthroughSupported = session.environmentBlendMode === 'alpha-blend' || session.environmentBlendMode === 'additive';

        this.isPassthroughActive = true;
        this.togglePassthrough(true);

        this.passthroughButton.visible = passthroughSupported;
        this.sfxButton.visible = true;
        this.mainActionButton.visible = true;
        this.uiGroup.visible = true;

        this.desktopLight.visible = false;
        this.vrLight.visible = true;
        if (this.score3d) this.score3d.mesh.visible = true;

        if (this.gameMover) this.gameMover.activate();

        this.restart();
    }

    onExitXR = () => {
        this.ui2d.container.style.display = 'flex';

        this.gameGroup.position.set(0, 0, 0);
        this.gameGroup.scale.set(1, 1, 1);

        this.passthroughButton.visible = false;
        this.sfxButton.visible = false;
        this.mainActionButton.visible = false;
        this.uiGroup.visible = false;
        if (this.score3d) this.score3d.mesh.visible = false;

        this.scene.background = new THREE.Color(0x7ec0ee); // Restore original background

        // Restore desktop camera position
        this.camera.position.copy(this.config.camera.position);
        this.camera.lookAt(this.config.camera.lookAt);
        this.desktopLight.visible = true;
        this.vrLight.visible = false;
        if (this.gameMover) this.gameMover.deactivate();

        this.restart();
    }

    bindControls = () => {
        this.ui2d.start.addEventListener('click', this.start);
        this.ui2d.restart.addEventListener('click', this.start);
        document.addEventListener('keydown', this.handleKeyDown);
        document.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        document.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    }

    start = () => {
        this.restart();
        this.gameEnded = false;
        this.lastTime = performance.now();

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = false;
        } else {
            this.ui2d.start.style.display = 'none';
            this.ui2d.restart.style.display = 'none';
            this.ui2d.score.style.display = 'block';
        }
    }

    // In CrossyRoad.js

    restart = () => {
        this.gameEnded = true;
        this.score = 0;
        this.maxZ = 0;
        this.playerGridPos = { x: 0, z: 0 };
        this.currentChunk = { type: 'road', remaining: 0 };
        this.lastWaterDirection = 1;

        gsap.globalTimeline.clear();

        // --- SAFETY NET CLEANUP ---
        // --- MODIFIED: Add this.player to the cleanup list ---
        const allObjectsToDispose = [...this.cars, ...this.logs, ...this.trains, ...this.lanes];
        if (this.player) {
            allObjectsToDispose.push(this.player);
        }
        // --- END OF MODIFICATION ---

        allObjectsToDispose.forEach(obj => {
            if (obj && obj.parent) {
                obj.removeFromParent();
            }
            // Use optional chaining for safety in case traverse doesn't exist
            obj.traverse?.(child => {
                if (child.isMesh) {
                    child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(material => material.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
        });

        // Now that objects are disposed, we can safely clear the state
        this.cars = [];
        this.trains = [];
        this.logs = [];
        this.coins = [];
        this.lanes = [];
        this.player = null; // --- ADDED: Explicitly nullify the player reference ---
        // --- END OF SAFETY NET ---

        // Remove the old world container and create a new one
        if (this.worldGroup) {
            this.gameGroup.remove(this.worldGroup);
        }
        this.worldGroup = new THREE.Group();
        this.gameGroup.add(this.worldGroup);

        // This will now create a fresh player object
        this.spawnPlayer();
        this.generateInitialWorld();

        this.updateScore(0);

        if (!this.renderer.xr.isPresenting) {
            this.camera.position.copy(this.config.camera.position);
            if (this.player) {
                this.camera.lookAt(this.player.position);
            } else {
                this.camera.lookAt(this.config.camera.lookAt);
            }
        }

        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = true;
            this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.play);
        } else {
            this.ui2d.start.style.display = 'block';
            this.ui2d.restart.style.display = 'none';
            this.ui2d.score.style.display = 'none';
        }
    }

    onResize = () => {
        const { clientWidth, clientHeight } = this.gameContainer;
        this.renderer.setSize(clientWidth, clientHeight);
        this.camera.aspect = clientWidth / clientHeight;
        this.camera.updateProjectionMatrix();
    }

    // MODIFIED: All game objects are added to the gameGroup
    spawnPlayer = () => {
        const { size } = this.config.player;
        this.player = new THREE.Mesh(
            new THREE.BoxGeometry(size, size, size),
            new THREE.MeshStandardMaterial({ color: 0xffd700 })
        );
        this.player.castShadow = true;
        this.player.position.set(0, size / 2, 0);
        this.player.userData.onLog = null;
        this.worldGroup.add(this.player);
    }

    // ... (movePlayer, onPlayerDeath, key/touch handlers are unchanged) ...
    movePlayer = (dx, dz) => {
        if (this.gameEnded || gsap.isTweening(this.player.position)) return;

        let targetGridX, targetGridZ;
        let isJumpingOffLog = false;

        //FALLING OFF THE BACK
        //     if (dz === -1) {
        //     // We need at least one lane to check against.
        //     if (this.lanes.length > 0) {
        //         // Get the Z position of the oldest lane currently in the world.
        //         const oldestLaneZ = this.lanes[0].userData.z;

        //         // Calculate where the player is trying to jump.
        //         const targetGridZ = this.playerGridPos.z + dz;

        //         // If the target is behind the oldest lane, the player falls off.
        //         if (targetGridZ < oldestLaneZ) {
        //             this.onPlayerDeath(false); // Call death function (not drowned)

        //             // Optional: Add a specific visual for falling off the back
        //             gsap.to(this.player.position, {
        //                 z: this.player.position.z + 2, // A small lurch backwards off the screen
        //                 y: -1, // Fall down
        //                 duration: 0.4,
        //                 ease: 'power2.in'
        //             });

        //             return; // CRITICAL: Stop the rest of the move function from executing.
        //         }
        //     }
        // }
        if (this.player.userData.onLog) {
            const log = this.player.userData.onLog;
            const currentGridX = Math.round(this.player.position.x);

            if (dz !== 0) {
                // ... (this part is fine)
                isJumpingOffLog = true;
                this.player.userData.onLog = null;
                targetGridX = currentGridX;
                targetGridZ = this.playerGridPos.z + dz;

            } else { // --- This is the section we are fixing (moving left/right on a log) ---
                targetGridX = currentGridX + dx;
                targetGridZ = this.playerGridPos.z;

                // --- LOCAL SPACE VALIDATION ---
                const logLocalX = log.position.x;
                const logLocalWidth = log.geometry.parameters.width;
                const logMinX = logLocalX - logLocalWidth / 2;
                const logMaxX = logLocalX + logLocalWidth / 2;

                // Check if the player's NEW target X position is still on the log
                // We give it a little tolerance with the player's size.
                const playerHalfWidth = this.config.player.size / 2;
                if (targetGridX - playerHalfWidth < logMinX || targetGridX + playerHalfWidth > logMaxX) {
                    // The move would take the player off the side of the log, so cancel it.
                    return;
                }
                // --- END OF LOCAL SPACE VALIDATION ---
            }
        } else {
            targetGridX = this.playerGridPos.x + dx;
            targetGridZ = this.playerGridPos.z + dz;
        }

        const halfWidth = Math.floor(this.config.world.laneWidth / 2);
        if (!this.player.userData.onLog) {
            if (targetGridX < -halfWidth || targetGridX > halfWidth || this.isTreeAt(targetGridX, targetGridZ)) {
                if (isJumpingOffLog) {
                    this.player.userData.onLog = this.getLogAt(this.player.position.x, this.playerGridPos.z);
                }
                return;
            }
        }

        const targetPos = new THREE.Vector3(targetGridX, this.config.player.size / 2, -targetGridZ);

        const tl = gsap.timeline({
            onComplete: () => {
                this.playerGridPos.x = targetGridX;
                this.playerGridPos.z = targetGridZ;
                this.checkCoinCollection(targetGridX, targetGridZ);
                if (!this.player.userData.onLog) {
                    const targetLane = this.lanes.find(l => l.userData.z === targetGridZ);
                    if (targetLane && targetLane.userData.type === 'water') {
                        const logUnderneath = this.getLogAt(this.player.position.x, targetGridZ);
                        if (logUnderneath) {
                            this.player.userData.onLog = logUnderneath;
                        } else {
                            this.onPlayerDeath(true);
                            return;
                        }
                    }
                }
            }
        });

        tl.to(this.player.position, { duration: this.config.player.hopDuration / 2, y: this.config.player.hopHeight + this.config.player.size / 2, ease: "power2.out" })
            .to(this.player.position, { duration: this.config.player.hopDuration / 2, y: targetPos.y, ease: "power2.in" });
        gsap.to(this.player.position, { duration: this.config.player.hopDuration, x: targetPos.x, z: targetPos.z, ease: "power2.out" });

        // --- START: MODIFIED CODE FOR VR TREADMILL ---
        // If in VR mode, move the whole game world to counteract the player's Z movement.
        if (this.renderer.xr.isPresenting && dz !== 0) {
            // 1. Get the world-space direction vector of the game's "forward" (-Z) axis.
            // This automatically accounts for the gameGroup's Y-axis rotation.
            const forwardDirection = new THREE.Vector3();
            this.worldGroup.getWorldDirection(forwardDirection); // Populates the vector with the direction

            const localMoveDistance = dz;

            // 4. Animate the gameGroup's position by adding the calculated move vector.
            // Using relative "+=" values makes the tween clean and robust.
            gsap.to(this.worldGroup.position, {
                z: `+=${localMoveDistance}`,
                duration: this.config.player.hopDuration,
                ease: "power2.out" // Match the player's hop easing
            });
        }
        if (dz === 1) this.player.rotation.y = 0;
        if (dz === -1) this.player.rotation.y = Math.PI;
        if (dx === 1) this.player.rotation.y = -Math.PI / 2;
        if (dx === -1) this.player.rotation.y = Math.PI / 2;

        if (targetGridZ > this.maxZ) {
            this.maxZ = targetGridZ;
            this.updateScore(this.score + 1);
        }
        this.updateWorld();
    }

    onPlayerDeath = (drowned = false) => {
        if (this.gameEnded) return;
        this.gameEnded = true;
        this.playSound('death');
        this.player.userData.onLog = null;
        if (drowned) {
            gsap.to(this.player.position, { y: -1, duration: 0.5, ease: 'power2.in' });
            gsap.to(this.player.rotation, { z: Math.PI, duration: 0.5 });
        } else {
            gsap.to(this.player.scale, { y: 0.1, duration: 0.2 });
            gsap.to(this.player.position, { y: 0.05, duration: 0.2 });
        }
        setTimeout(() => this.endGame(), 500);
    }

    handleKeyDown = (event) => {
        if (this.gameEnded) return;
        switch (event.key) {
            case 'ArrowUp': case 'w': this.movePlayer(0, 1); break;
            case 'ArrowDown': case 's': this.movePlayer(0, -1); break;
            case 'ArrowLeft': case 'a': this.movePlayer(-1, 0); break;
            case 'ArrowRight': case 'd': this.movePlayer(1, 0); break;
        }
    }

    handleTouchStart = (event) => {
        if (event.target.closest('button') || event.target.closest('.ui-element')) {
            return;
        }
        event.preventDefault();
        this.touchStartPos.x = event.touches[0].clientX;
        this.touchStartPos.y = event.touches[0].clientY;
    };

    handleTouchEnd = (event) => {
        if (event.target.closest('.ui-element')) return;
        if (this.gameEnded) return;

        const touchEndPos = { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
        const dx = touchEndPos.x - this.touchStartPos.x;
        const dy = touchEndPos.y - this.touchStartPos.y;
        const swipeThreshold = 50;

        if (Math.abs(dx) < swipeThreshold && Math.abs(dy) < swipeThreshold) {
            this.movePlayer(0, 1);
            return;
        }

        if (Math.abs(dx) > Math.abs(dy)) {
            if (Math.abs(dx) > swipeThreshold) dx > 0 ? this.movePlayer(1, 0) : this.movePlayer(-1, 0);
        } else {
            if (Math.abs(dy) > swipeThreshold) dy > 0 ? this.movePlayer(0, -1) : this.movePlayer(0, 1);
        }
    };

    // MODIFIED: Adds all new objects to the gameGroup.
    generateLane = (z) => {
        // ... (chunk selection logic is unchanged) ...
        if (this.currentChunk.remaining <= 0) {
            const possibleTypes = ['grass', 'road', 'water'];
            if (z < 10) possibleTypes.pop();
            if (this.currentChunk.type === 'train') possibleTypes.splice(possibleTypes.indexOf('train'), 1);

            const isNextTrain = Math.random() < 0.2 && this.currentChunk.type !== 'train' && z > 10;
            if (isNextTrain) {
                this.currentChunk = { type: 'train', remaining: 1, direction: Math.random() > 0.5 ? 1 : -1, speed: this.config.train.speed * THREE.MathUtils.randFloat(0.9, 1.2) };
            } else {
                let nextType;
                do { nextType = possibleTypes[Math.floor(Math.random() * possibleTypes.length)]; } while (nextType === this.currentChunk.type)

                const chunkConfig = this.config.world.laneChunks[nextType];
                this.currentChunk = {
                    type: nextType,
                    remaining: THREE.MathUtils.randInt(chunkConfig.min, chunkConfig.max),
                    direction: Math.random() > 0.5 ? 1 : -1,
                    ...(nextType === 'road' && { speed: this.config.obstacle.road.carTypes.speed * THREE.MathUtils.randFloat(0.8, 1.5) }),
                    ...(nextType === 'water' && { speed: this.config.obstacle.log.logType.speed * THREE.MathUtils.randFloat(0.8, 1.2) }),
                };
            }
        }
        this.currentChunk.remaining--;

        const laneType = this.currentChunk.type;
        const laneGroup = new THREE.Group();
        laneGroup.position.z = -z;

        let userData = { type: laneType, z: z, obstacles: [] };
        laneGroup.userData = userData;

        const groundColor = laneType === 'grass' ? 0x89c149 : (laneType === 'road' ? 0x4d4d4d : (laneType === 'train' ? 0x3a3a3a : 0x4da6ff));
        const ground = new THREE.Mesh(new THREE.BoxGeometry(this.config.world.laneWidth, 0.2, 1.0), new THREE.MeshStandardMaterial({ color: groundColor, transparent: laneType === 'water', opacity: laneType === 'water' ? 0.8 : 1.0 }));
        ground.receiveShadow = true;
        ground.position.y = -0.1;
        laneGroup.add(ground);

        if (laneType === 'grass' && z > 1) {
            this.spawnGrassObstaclesForLane(laneGroup);
            this.spawnCoinForLane(laneGroup);
        } else if (laneType === 'road') {
            const obstacleConfig = this.config.obstacle.road.traffic;
            Object.assign(userData, { direction: Math.random() > 0.5 ? 1 : -1, speed: this.currentChunk.speed, nextSpawnTime: performance.now() + THREE.MathUtils.randFloat(obstacleConfig.spawnCooldown.min, obstacleConfig.spawnCooldown.max) * 1000 });
        } else if (laneType === 'water') {
            const direction = -this.lastWaterDirection;
            this.lastWaterDirection = direction;
            const obstacleConfig = this.config.obstacle.log.traffic;
            Object.assign(userData, { direction: direction, speed: this.currentChunk.speed, nextSpawnTime: performance.now() + THREE.MathUtils.randFloat(obstacleConfig.spawnCooldown.min, obstacleConfig.spawnCooldown.max) * 1000 });
        } else if (laneType === 'train') {
            laneGroup.add(this.createTrainTrack());
            const warningLights = [this.createWarningLight(), this.createWarningLight()];
            const lightDist = this.config.world.laneWidth / 2 + 0.5;
            warningLights[0].position.set(-lightDist, 0.5, 0);
            warningLights[1].position.set(lightDist, 0.5, 0);
            laneGroup.add(...warningLights);
            Object.assign(userData, { warningLights, direction: this.currentChunk.direction, speed: this.currentChunk.speed, isWarning: false, nextSpawnTime: performance.now() + THREE.MathUtils.randFloat(this.config.train.spawnCooldown.min, this.config.train.spawnCooldown.max) * 1000 });
        }

        if (laneType === 'road' || laneType === 'water') {
            this.prepopulateLane(laneGroup);
        }

        // MODIFIED
        this.worldGroup.add(laneGroup);
        this.lanes.push(laneGroup);
    }

    prepopulateLane = (laneGroup) => {
        // ... (unchanged logic) ...
        const { type, z, direction, speed } = laneGroup.userData;
        const halfLaneWidth = this.config.world.laneWidth / 2;
        let currentX = -halfLaneWidth;

        while (currentX < halfLaneWidth) {
            let config, obstacle, length;

            if (type === 'road') {
                config = this.config.obstacle.road;
                if (Math.random() < config.initialDensity) {
                    const carType = this.getRandomCarType();
                    length = config.carTypes[carType].length;
                    obstacle = this.createVehicle(carType);

                    if (direction === 1) {
                        obstacle.rotation.y = Math.PI;
                    }

                    obstacle.userData = { z, direction, speed, type: carType, length };
                    this.cars.push(obstacle);
                }
                currentX += length ? length + config.minSpacing * Math.random() : 3 + Math.random() * 2;
            } else if (type === 'water') {
                config = this.config.obstacle.log;
                if (Math.random() < config.initialDensity) {
                    length = THREE.MathUtils.randFloat(config.logType.length.min, config.logType.length.max);
                    obstacle = this.createLog(length);
                    obstacle.userData = { z, direction, speed, type: 'log', length };
                    this.logs.push(obstacle);
                }
                currentX += length ? length + config.minSpacing * Math.random() : 3 + Math.random() * 2;
            } else {
                break;
            }

            if (obstacle) {
                const yPos = type === 'road' ? 0 : obstacle.position.y;
                obstacle.position.set(currentX - halfLaneWidth, yPos, -z);
                // MODIFIED
                this.worldGroup.add(obstacle);
            }
        }
    }

    // MODIFIED: All spawned objects go into gameGroup
    spawnCarOnLane = (lane) => {
        // ... (unchanged logic, except for the final scene.add) ...
        const { z, direction } = lane.userData;
        const carType = this.getRandomCarType();
        const carLength = this.config.obstacle.road.carTypes[carType].length;
        const halfLaneWidth = this.config.world.laneWidth / 2;
        const spawnEdgeX = (halfLaneWidth + carLength) * direction;

        const carsOnLane = this.cars.filter(c => c.userData.z === z);
        for (const carOnLane of carsOnLane) {
            if (Math.abs(carOnLane.position.x - spawnEdgeX) < carLength * 1.5) return;
        }

        const car = this.createVehicle(carType);

        if (direction === 1) {
            car.rotation.y = Math.PI;
        }

        car.position.set(spawnEdgeX, 0, -z);
        car.userData = { ...lane.userData, type: carType, length: carLength };

        this.worldGroup.add(car); // MODIFIED
        this.cars.push(car);
    }

    spawnLogOnLane = (lane) => {
        // ... (unchanged logic, except for the final scene.add) ...
        const { z, direction } = lane.userData;
        const { min, max } = this.config.obstacle.log.logType.length;
        const newLogLength = THREE.MathUtils.randFloat(min, max);

        const halfLaneWidth = this.config.world.laneWidth / 2;
        const spawnEdgeX = (halfLaneWidth + newLogLength / 2) * direction;

        const logsOnLane = this.logs.filter(l => l.userData.z === z);
        for (const logOnLane of logsOnLane) {
            const existingLogHalfLength = logOnLane.userData.length / 2;
            const newLogHalfLength = newLogLength / 2;
            const requiredSeparation = existingLogHalfLength + newLogHalfLength + 1.5;

            if (Math.abs(logOnLane.position.x - spawnEdgeX) < requiredSeparation) { return; }
        }

        const log = this.createLog(newLogLength);
        log.position.set(spawnEdgeX, log.position.y, -z);
        log.userData = { ...lane.userData, type: 'log', length: newLogLength };

        this.worldGroup.add(log); // MODIFIED
        this.logs.push(log);
    }

    spawnTrainOnLane = (lane) => {
        // ... (unchanged logic, except for the final scene.add) ...
        const { z, direction } = lane.userData;
        const { trainGroup, totalLength } = this.createTrain();

        const halfLaneWidth = this.config.world.laneWidth / 2;
        const spawnEdgeX = (halfLaneWidth + totalLength / 2) * direction;

        if (direction === 1) {
            trainGroup.rotation.y = Math.PI;
        }

        trainGroup.position.set(spawnEdgeX, 0, -z);
        trainGroup.userData = { ...lane.userData, type: 'train', length: totalLength };

        this.worldGroup.add(trainGroup); // MODIFIED
        this.trains.push(trainGroup);
    }

    updateWorld = () => {
        const playerZ = this.playerGridPos.z;
        const farthestLaneZ = this.lanes.length > 0 ? this.lanes[this.lanes.length - 1].userData.z : 0;

        if (farthestLaneZ - playerZ < this.config.world.laneCount - 5) {
            this.generateLane(farthestLaneZ + 1);
        }

        // Check if the first lane is too far behind the player
        if (this.lanes.length > 0) {
            const oldestLane = this.lanes[0];
            if (playerZ - oldestLane.userData.z > 10) {
                this.removeObject(oldestLane, 'lane');

                // Also clean up any associated objects that might be orphaned
                const removedZ = oldestLane.userData.z;
                this.cars.filter(car => car.userData.z === removedZ).forEach(car => this.removeObject(car, 'car'));
                this.trains.filter(train => train.userData.z === removedZ).forEach(train => this.removeObject(train, 'train'));
                this.logs.filter(log => log.userData.z === removedZ).forEach(log => this.removeObject(log, 'log'));
                this.coins = this.coins.filter(coin => coin.userData.z !== removedZ); // Coins are handled differently
            }
        }
    }

    endGame = () => {
        this.gameEnded = true;
        const finalScore = this.score || 0;
        if (this.renderer.xr.isPresenting) {
            this.mainActionButton.visible = true;
            this.mainActionButton.userData.iconMaterial.map = this.textureLoader.load(this.config.ui.iconPaths.restart);
            if (this.leaderboard) this.leaderboard.submit(finalScore);
        } else {
            this.ui2d.score.style.display = 'none';
            if (this.leaderboard && finalScore > 0) {
                this.leaderboard.submitAndShow(finalScore, () => {
                    this.ui2d.restart.click();
                });
            } else {
                this.ui2d.restart.style.display = 'block';
            }
        }
    };

    // --- NEW: All XR Control and UI methods ---

    setupXrControls = () => {
        this.raycaster = new THREE.Raycaster();

        for (let i = 0; i < 2; i++) {
            const controller = this.renderer.xr.getController(i);
            controller.addEventListener('selectstart', () => this.handleControllerSelect(controller));
            controller.addEventListener('connected', (event) => {
                // Initialize input state when controller connects
                controller.userData.gamepad = event.data.gamepad;
                controller.userData.inputState = { x: 'neutral', z: 'neutral' };
            });
            this.scene.add(controller);

            const pointerGeometry = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]);
            const pointer = new THREE.Line(pointerGeometry);
            pointer.name = 'pointer';
            pointer.scale.z = 5;
            controller.add(pointer);
            this.controllers.push(controller);
        }
    }

    handleControllerSelect = (controller) => {
        const intersections = this.getIntersections(controller);
        if (intersections.length > 0) {
            const button = intersections[0].object.parent; // The group is the button
            this.animateButtonClick(button);
            this.playSound('click');

            if (button === this.mainActionButton && this.gameEnded) {
                this.start();
            } else if (button === this.passthroughButton) {
                this.togglePassthrough();
            } else if (button === this.sfxButton) {
                this.toggleSfxMute();
            }
        }
    }

    create3dUi = () => {
        this.uiGroup = new THREE.Group();
        this.uiGroup.position.set(0, 1.5, -0.5); // Position UI relative to the game group's center
        this.gameGroup.add(this.uiGroup);

        this.mainActionButton = this.createUiButton(this.config.ui.iconPaths.play, this.config.ui.buttonSize * 1.5);
        this.mainActionButton.position.set(0, 2, -5);
        this.uiGroup.add(this.mainActionButton);

        this.passthroughButton = this.createUiButton(this.config.ui.iconPaths.passthroughShow);
        this.passthroughButton.position.set(7, 0, -5);
        this.uiGroup.add(this.passthroughButton);

        this.sfxButton = this.createUiButton(this.config.ui.iconPaths.sfxOn);
        this.sfxButton.position.set(7, -1.5, -5);
        this.uiGroup.add(this.sfxButton);

        this.uiGroup.visible = false;
    }

    create3dScore = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512; canvas.height = 128;
        const context = canvas.getContext('2d');
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
        const planeHeight = 2;
        const planeWidth = planeHeight * (canvas.width / canvas.height);
        const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(0, 6, -5);
        mesh.visible = false;
        mesh.raycast = () => { }; // Make it un-clickable
        this.gameGroup.add(mesh);
        this.score3d = { canvas, context, mesh };
    }

    createUiButton = (iconPath, size = this.config.ui.buttonSize) => {
        const button = new THREE.Group();
        const background = new THREE.Mesh(
            new THREE.CircleGeometry(size / 2, 32),
            new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5 })
        );
        button.add(background);

        const iconMaterial = new THREE.MeshBasicMaterial({ map: this.textureLoader.load(iconPath), transparent: true });
        const icon = new THREE.Mesh(new THREE.PlaneGeometry(size * 0.7, size * 0.7), iconMaterial);
        icon.position.z = 0.01;
        button.add(icon);
        button.userData.iconMaterial = iconMaterial;
        return button;
    }

    animateButtonClick = (button) => {
        gsap.timeline()
            .to(button.scale, { x: 0.8, y: 0.8, z: 0.8, duration: 0.1 })
            .to(button.scale, { x: 1, y: 1, z: 1, duration: 0.1 });
    }

    togglePassthrough = (forceState) => {
        this.isPassthroughActive = forceState !== undefined ? forceState : !this.isPassthroughActive;
        this.scene.background = this.isPassthroughActive ? null : new THREE.Color(0x7ec0ee);

        const iconPath = this.isPassthroughActive ? this.config.ui.iconPaths.passthroughHide : this.config.ui.iconPaths.passthroughShow;
        this.passthroughButton.userData.iconMaterial.map = this.textureLoader.load(iconPath);
    }

    toggleSfxMute = () => {
        this.isSfxMuted = !this.isSfxMuted;
        this.audioListener.setMasterVolume(this.isSfxMuted ? 0 : 1);
        const newIconPath = this.isSfxMuted ? this.config.ui.iconPaths.sfxOff : this.config.ui.iconPaths.sfxOn;
        this.sfxButton.userData.iconMaterial.map = this.textureLoader.load(newIconPath);
    }

    getIntersections = (controller) => {
        this.raycaster.setFromXRController(controller);
        return this.raycaster.intersectObjects(this.uiGroup.children, true);
    }

    playSound = (name) => {
        // Placeholder for your sound logic
        if (this.isSfxMuted) return;
        // e.g., if (this.sounds[name]) this.sounds[name].play();
        console.log(`Playing sound (stub): ${name}`);
    }

    // --- Main Animation Loop ---
    animation = (time) => {

        if (!this.lastTime) this.lastTime = time;
        const deltaTime = (time - this.lastTime) / 1000;
        this.lastTime = time;

        gsap.ticker.tick();
        this.updateClippingPlanes();
        if (this.renderer.xr.isPresenting) {
            this.updateControllerInteractions();
            this.handleXrPlayerMovement();
            if (this.gameMover) this.gameMover.update();
            // In VR, the camera is controlled by the user, so we don't move it manually.
        }

        if (!this.gameEnded) {
            this.updateActiveLight();
            if (!this.renderer.xr.isPresenting) {
                const targetCameraZ = this.player.position.z + this.config.camera.position.z;
                gsap.to(this.camera.position, { z: targetCameraZ, duration: 0.5 });
                gsap.to(this.camera.position, { x: this.player.position.x * 0.2, duration: 0.5 });

                if (this.dirLight && this.player) {
                    // 1. Center the light's *target* on the player. This centers the shadow map.
                    this.dirLight.target.position.copy(this.player.position);

                    // 2. Keep the light source at a fixed offset *from the new target*.
                    //    This ensures the light direction remains constant.
                    this.dirLight.position.copy(this.dirLight.target.position).add(new THREE.Vector3(-5, 10, 5));

                    // 3. We must update the target's matrix world for the changes to take effect.
                    this.dirLight.target.updateMatrixWorld();
                }
            } else {
                // In VR, keep the light relative to the player inside the gameGroup
                if (this.dirLight && this.player) {
                    this.dirLight.position.set(this.player.position.x - 5, 10, this.player.position.z + 5);
                    this.dirLight.target.position.copy(this.player.position);
                }
            }

            this.updateSpawners(time);
            this.updateCars(deltaTime);
            this.updateTrains(deltaTime);
            this.updateLogs(deltaTime);
            this.updateCoins(deltaTime);
            this.checkCollisions();

            // Check if player fell off the back
            if (!this.renderer.xr.isPresenting) {
                if (this.player.position.z < this.camera.position.z - this.config.camera.position.z - 5) { this.onPlayerDeath(); }
            } else {
                // In VR, check against a fixed Z distance behind the start
                if (this.playerGridPos.z < -10) { this.onPlayerDeath(); }
            }
        }

        this.renderer.render(this.scene, this.camera);

    }

    handleXrPlayerMovement = () => {
        if (this.gameEnded) return;

        for (const controller of this.controllers) {
            if (controller.userData.gamepad && controller.userData.inputState) {
                const gamepad = controller.userData.gamepad;
                const state = controller.userData.inputState;
                const { controllerDeadZone: deadZone, controllerThreshold: threshold } = this.config.vr;

                // Axes 2 and 3 are typically the thumbstick
                const xAxis = gamepad.axes[2] || 0;
                const zAxis = gamepad.axes[3] || 0;

                // --- Handle Z-axis movement (Up/Down) ---
                if (zAxis < -threshold && state.z === 'neutral') {
                    this.movePlayer(0, 1);
                    state.z = 'pressed';
                } else if (zAxis > threshold && state.z === 'neutral') {
                    this.movePlayer(0, -1);
                    state.z = 'pressed';
                } else if (Math.abs(zAxis) < deadZone && state.z === 'pressed') {
                    state.z = 'neutral';
                }

                // --- Handle X-axis movement (Left/Right) ---
                if (xAxis < -threshold && state.x === 'neutral') {
                    this.movePlayer(-1, 0);
                    state.x = 'pressed';
                } else if (xAxis > threshold && state.x === 'neutral') {
                    this.movePlayer(1, 0);
                    state.x = 'pressed';
                } else if (Math.abs(xAxis) < deadZone && state.x === 'pressed') {
                    state.x = 'neutral';
                }
            }
        }
    }

    updateControllerInteractions = () => {
        const intersectingButtons = new Set();
        let isHoveringMover = false;

        for (const controller of this.controllers) {
            this.raycaster.setFromXRController(controller);

            const uiIntersections = this.raycaster.intersectObjects(this.uiGroup.children, true);
            if (uiIntersections.length > 0) {
                intersectingButtons.add(uiIntersections[0].object.parent);
            }

            if (this.gameMover?.grabHandle) {
                if (this.raycaster.intersectObject(this.gameMover.grabHandle).length > 0) {
                    isHoveringMover = true;
                }
            }
        }

        if (this.gameMover) {
            this.gameMover.setHoverState(isHoveringMover);
        }

        [this.mainActionButton, this.passthroughButton, this.sfxButton].forEach(button => {
            if (!button) return;
            const isHit = intersectingButtons.has(button);
            gsap.to(button.scale, { x: isHit ? 1.2 : 1, y: isHit ? 1.2 : 1, z: 1, duration: 0.2 });
        });
    }

    updateScore = (newScore) => {
        this.score = newScore;
        // Update both 2D and 3D scores
        this.ui2d.score.textContent = `Score: ${this.score}`;

        if (this.score3d?.context) {
            const { context, canvas, mesh } = this.score3d;
            const text = `Score: ${this.score}`;
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.font = 'bold 60px Arial';
            context.fillStyle = 'white';
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(text, canvas.width / 2, canvas.height / 2);
            if (mesh.material.map) {
                mesh.material.map.needsUpdate = true;
            }
        }
    };

    // --- All other methods (createTree, createCar, etc.) are unchanged ---
    // ... (include the full, unchanged methods from the original file here) ...
    createTree = () => {
        const tree = new THREE.Group();
        const { trunkRadius, trunkHeight, crownRadius } = this.config.obstacle.tree;
        const randomTrunkHeight = THREE.MathUtils.randFloat(trunkHeight.min, trunkHeight.max);

        // Use BoxGeometry for the trunk
        const trunkWidth = trunkRadius * 1.5;
        const trunk = new THREE.Mesh(
            new THREE.BoxGeometry(trunkWidth, randomTrunkHeight, trunkWidth),
            new THREE.MeshStandardMaterial({ color: 0x8B4513, flatShading: true })
        );
        trunk.position.y = randomTrunkHeight / 2;
        trunk.castShadow = true;

        // Use BoxGeometry for the crown
        const crownSize = crownRadius * 1.8;
        const crown = new THREE.Mesh(
            new THREE.BoxGeometry(crownSize, crownSize, crownSize),
            new THREE.MeshStandardMaterial({ color: 0x228B22, flatShading: true })
        );
        crown.position.y = randomTrunkHeight + crownSize * 0.45; // Position it nicely on the trunk
        crown.castShadow = true;

        tree.add(trunk, crown);
        return tree;
    };
    createBush = () => {
        const { radius, color } = this.config.obstacle.bush;
        const bushSize = radius * 1.8; // Make it a nice size
        const bush = new THREE.Mesh(
            new THREE.BoxGeometry(bushSize, bushSize, bushSize),
            new THREE.MeshStandardMaterial({ color: color, flatShading: true })
        );
        bush.castShadow = true;
        bush.position.y = bushSize / 2; // Sit it on the ground
        return bush;
    };
    createCoin = () => {
        const { radius, height } = this.config.coin.geometry;
        const coin = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, height, 16), new THREE.MeshStandardMaterial({ color: 0xffd700, metalness: 0.5, roughness: 0.5 }));
        coin.castShadow = true; coin.rotation.x = Math.PI / 2;
        return coin;
    }
    createVehicle = (type) => {
        const vehicle = type === 'truck' ? this.createTruck() : this.createCar(type);
        vehicle.traverse(node => {
            if (node.isMesh) {
                node.castShadow = true;
                node.material.clippingPlanes = this.clippingPlanes;
            }
        });
        return vehicle;
    }
    createCar = (type) => {
        const car = new THREE.Group();
        const config = this.config.obstacle.road.carTypes;
        const typeConfig = config[type];
        const wheelRadius = config.wheel.radius;

        const bodyMat = new THREE.MeshStandardMaterial({ color: new THREE.Color().setHSL(Math.random(), 0.7, 0.5), flatShading: true });
        const body = new THREE.Mesh(new THREE.BoxGeometry(typeConfig.length, config.mainHeight, config.width), bodyMat);
        body.position.y = wheelRadius + config.mainHeight / 2;
        car.add(body);

        const roofGeo = new THREE.BoxGeometry(typeConfig.length * 0.6, config.mainHeight * 0.8, config.width * 0.8);
        const roof = new THREE.Mesh(roofGeo, new THREE.MeshStandardMaterial({ color: config.roofColor, flatShading: true }));
        roof.position.y = body.position.y + config.mainHeight * 0.6;
        car.add(roof);

        const windowMat = new THREE.MeshStandardMaterial({ color: 0x000000, flatShading: true });
        const roofGeoParams = roof.geometry.parameters;
        const windowDepth = 0.05;

        const frontWindowGeo = new THREE.BoxGeometry(windowDepth, roofGeoParams.height * 0.7, roofGeoParams.depth * 0.9);
        const frontWindow = new THREE.Mesh(frontWindowGeo, windowMat);
        frontWindow.position.set(roofGeoParams.width / 2 + roof.position.x, roof.position.y, 0);
        car.add(frontWindow);

        const sideWindowGeo = new THREE.BoxGeometry(roofGeoParams.width * 0.6, roofGeoParams.height * 0.7, windowDepth);
        const sideWindow1 = new THREE.Mesh(sideWindowGeo, windowMat);
        const sideWindow2 = new THREE.Mesh(sideWindowGeo, windowMat);
        sideWindow1.position.set(roof.position.x, roof.position.y, roofGeoParams.depth / 2);
        sideWindow2.position.set(roof.position.x, roof.position.y, -roofGeoParams.depth / 2);
        car.add(sideWindow1, sideWindow2);

        const wheelGeo = new THREE.BoxGeometry(wheelRadius * 2, wheelRadius * 2, config.wheel.width);
        const wheelMat = new THREE.MeshStandardMaterial({
            color: config.wheel.color, flatShading: true, polygonOffset: true,
            polygonOffsetFactor: -1.0, // Negative pulls it towards the camera
            polygonOffsetUnits: -4.0
        });
        const wheelPositions = [
            new THREE.Vector3(typeConfig.length / 2 * 0.8, wheelRadius, config.width / 2),
            new THREE.Vector3(typeConfig.length / 2 * 0.8, wheelRadius, -config.width / 2),
            new THREE.Vector3(-typeConfig.length / 2 * 0.8, wheelRadius, config.width / 2),
            new THREE.Vector3(-typeConfig.length / 2 * 0.8, wheelRadius, -config.width / 2),
        ];
        wheelPositions.forEach(pos => { const wheel = new THREE.Mesh(wheelGeo, wheelMat); wheel.position.copy(pos); car.add(wheel); });

        const headlightGeo = new THREE.BoxGeometry(config.headlight.size, config.headlight.size, 0.05);
        const headlightMat = new THREE.MeshBasicMaterial({ color: config.headlight.color });
        const headlights = [new THREE.Mesh(headlightGeo, headlightMat), new THREE.Mesh(headlightGeo, headlightMat)];
        headlights[0].position.set(typeConfig.length / 2, body.position.y, config.width * 0.3);
        headlights[1].position.set(typeConfig.length / 2, body.position.y, -config.width * 0.3);
        car.add(...headlights);

        return car;
    }
    createTruck = () => {
        const truck = new THREE.Group();
        const config = this.config.obstacle.road.carTypes;
        const typeConfig = config.truck;
        const wheelRadius = config.wheel.radius;

        const bodyMat = new THREE.MeshStandardMaterial({ color: new THREE.Color().setHSL(Math.random(), 0.7, 0.5), flatShading: true });

        const cabGeo = new THREE.BoxGeometry(typeConfig.cab.length, typeConfig.cab.height, config.width);
        const cab = new THREE.Mesh(cabGeo, bodyMat);
        cab.position.x = (typeConfig.length / 2) - (typeConfig.cab.length / 2);
        cab.position.y = wheelRadius + typeConfig.cab.height / 2;
        truck.add(cab);

        const windowMat = new THREE.MeshStandardMaterial({ color: 0x000000, flatShading: true });
        const cabGeoParams = cab.geometry.parameters;
        const windowDepth = 0.05;

        const frontWindowGeo = new THREE.BoxGeometry(windowDepth, cabGeoParams.height * 0.5, cabGeoParams.depth * 0.9);
        const frontWindow = new THREE.Mesh(frontWindowGeo, windowMat);
        frontWindow.position.set(cab.position.x + cabGeoParams.width / 2, cab.position.y + cabGeoParams.height * 0.1, 0);
        truck.add(frontWindow);

        const sideWindowGeo = new THREE.BoxGeometry(cabGeoParams.width * 0.5, cabGeoParams.height * 0.5, windowDepth);
        const sideWindow1 = new THREE.Mesh(sideWindowGeo, windowMat);
        const sideWindow2 = new THREE.Mesh(sideWindowGeo, windowMat);
        sideWindow1.position.set(cab.position.x, cab.position.y + cabGeoParams.height * 0.1, cabGeoParams.depth / 2);
        sideWindow2.position.set(cab.position.x, cab.position.y + cabGeoParams.height * 0.1, -cabGeoParams.depth / 2);
        truck.add(sideWindow1, sideWindow2);

        const trailerMat = new THREE.MeshStandardMaterial({ color: 0xdddddd, flatShading: true });
        const trailer = new THREE.Mesh(new THREE.BoxGeometry(typeConfig.trailer.length, typeConfig.trailer.height, config.width * 1.05), trailerMat);
        trailer.position.x = -(typeConfig.length / 2) + (typeConfig.trailer.length / 2);
        trailer.position.y = wheelRadius + typeConfig.trailer.height / 2;
        truck.add(trailer);

        const wheelGeo = new THREE.BoxGeometry(wheelRadius * 2, wheelRadius * 2, config.wheel.width);
        const wheelMat = new THREE.MeshStandardMaterial({ color: config.wheel.color, flatShading: true });
        const wheelPositions = [
            new THREE.Vector3(cab.position.x + typeConfig.cab.length * 0.3, wheelRadius, config.width / 2),
            new THREE.Vector3(cab.position.x + typeConfig.cab.length * 0.3, wheelRadius, -config.width / 2),
            new THREE.Vector3(trailer.position.x + typeConfig.trailer.length * 0.3, wheelRadius, config.width / 2),
            new THREE.Vector3(trailer.position.x + typeConfig.trailer.length * 0.3, wheelRadius, -config.width / 2),
            new THREE.Vector3(trailer.position.x - typeConfig.trailer.length * 0.3, wheelRadius, config.width / 2),
            new THREE.Vector3(trailer.position.x - typeConfig.trailer.length * 0.3, wheelRadius, -config.width / 2),
        ];
        wheelPositions.forEach(pos => { const wheel = new THREE.Mesh(wheelGeo, wheelMat); wheel.position.copy(pos); truck.add(wheel); });

        const headlightGeo = new THREE.BoxGeometry(config.headlight.size, config.headlight.size, 0.05);
        const headlightMat = new THREE.MeshBasicMaterial({ color: config.headlight.color });
        const headlights = [new THREE.Mesh(headlightGeo, headlightMat), new THREE.Mesh(headlightGeo, headlightMat)];
        headlights[0].position.set(cab.position.x + typeConfig.cab.length / 2, cab.position.y - typeConfig.cab.height * 0.2, config.width * 0.3);
        headlights[1].position.set(cab.position.x + typeConfig.cab.length / 2, cab.position.y - typeConfig.cab.height * 0.2, -config.width * 0.3);
        truck.add(...headlights);

        return truck;
    }
    createLog = (length) => {
        const logConfig = this.config.obstacle.log.logType;
        const log = new THREE.Mesh(new THREE.BoxGeometry(length, logConfig.height, logConfig.width), new THREE.MeshStandardMaterial({ color: 0x966F33, flatShading: true, clippingPlanes: this.clippingPlanes, }));
        log.castShadow = true; log.position.y = logConfig.height / 2 - 0.2;
        return log;
    }
    createTrain = () => {
        const trainGroup = new THREE.Group();
        const config = this.config.train.visuals;
        const locoConfig = config.locomotive;
        const carConfig = config.car;
        const wheelRadius = config.wheel.radius;
        const windowMat = new THREE.MeshStandardMaterial({ color: 0x000000, flatShading: true });

        const numCars = THREE.MathUtils.randInt(config.carCount.min, config.carCount.max);
        let currentX = 0;

        // Define wheel geometry and material once for reuse
        const wheelGeo = new THREE.BoxGeometry(wheelRadius, wheelRadius, config.wheel.width, 16);
        wheelGeo.rotateX(Math.PI / 2); // Rotate to align with track
        const wheelMat = new THREE.MeshStandardMaterial({ color: config.wheel.color });

        // --- 1. Build Train Cars FIRST ---
        for (let i = 0; i < numCars; i++) {
            const carBodyGeo = new THREE.BoxGeometry(carConfig.length, carConfig.height, carConfig.width);
            const carBodyMat = new THREE.MeshStandardMaterial({ color: carConfig.color, flatShading: true });
            const carBody = new THREE.Mesh(carBodyGeo, carBodyMat);
            carBody.position.y = wheelRadius + carConfig.height / 2;
            carBody.position.x = currentX + carConfig.length / 2;
            trainGroup.add(carBody);

            // Add side windows to the car
            const sideWindowGeo = new THREE.BoxGeometry(carConfig.length * 0.7, carConfig.height * 0.4, 0.05);
            const sideWindow1 = new THREE.Mesh(sideWindowGeo, windowMat);
            const sideWindow2 = sideWindow1.clone();
            sideWindow1.position.set(carBody.position.x, carBody.position.y, carConfig.width / 2);
            sideWindow2.position.set(carBody.position.x, carBody.position.y, -carConfig.width / 2);
            trainGroup.add(sideWindow1, sideWindow2);

            // Car Wheels
            const carWheels = [new THREE.Mesh(wheelGeo.clone(), wheelMat), new THREE.Mesh(wheelGeo.clone(), wheelMat)];
            carWheels[0].position.set(carBody.position.x - carConfig.length * 0.3, wheelRadius, 0);
            carWheels[1].position.set(carBody.position.x + carConfig.length * 0.3, wheelRadius, 0);
            trainGroup.add(...carWheels);

            // Cargo
            const numCargo = THREE.MathUtils.randInt(config.cargo.count.min, config.cargo.count.max);
            for (let j = 0; j < numCargo; j++) {
                const cargoSize = THREE.MathUtils.randFloat(config.cargo.size.min, config.cargo.size.max);
                const cargoHeight = THREE.MathUtils.randFloat(config.cargo.height.min, config.cargo.height.max);
                const cargoGeo = new THREE.BoxGeometry(cargoSize, cargoHeight, cargoSize);
                const cargoMat = new THREE.MeshStandardMaterial({ color: 0xffffff, flatShading: true });
                const cargo = new THREE.Mesh(cargoGeo, cargoMat);

                const cargoXPos = carBody.position.x + (j === 0 ? -carConfig.length / 4 : carConfig.length / 4);
                cargo.position.set(cargoXPos, carBody.position.y + carConfig.height / 2 + cargoHeight / 2, 0);
                trainGroup.add(cargo);
            }

            currentX += carConfig.length + config.spacing;
        }

        // --- 2. Build Locomotive LAST ---
        const locoBodyGeo = new THREE.BoxGeometry(locoConfig.length, locoConfig.height, locoConfig.width);
        const locoBodyMat = new THREE.MeshStandardMaterial({ color: locoConfig.color, flatShading: true });
        const locoBody = new THREE.Mesh(locoBodyGeo, locoBodyMat);
        locoBody.position.y = wheelRadius + locoConfig.height / 2;
        locoBody.position.x = currentX + locoConfig.length / 2; // Position after all cars
        trainGroup.add(locoBody);

        // Locomotive Cab
        const cabGeo = new THREE.BoxGeometry(locoConfig.length * 0.4, locoConfig.cabHeight, locoConfig.width * 0.9);
        const cabMat = new THREE.MeshStandardMaterial({ color: 0x555555, flatShading: true });
        const cab = new THREE.Mesh(cabGeo, cabMat);
        cab.position.set(locoBody.position.x - locoConfig.length * 0.2, locoBody.position.y + locoConfig.height / 2 - locoConfig.cabHeight / 2, 0);
        trainGroup.add(cab);

        // Locomotive Cab Front Window
        const cabFrontWindow = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.4, 0.6), windowMat);
        cabFrontWindow.position.set(cab.position.x + (locoConfig.length * 0.2), cab.position.y, 0);
        trainGroup.add(cabFrontWindow);

        // Locomotive Headlights
        const headlightGeo = new THREE.BoxGeometry(locoConfig.headlight.size, locoConfig.headlight.size, 0.05);
        const headlightMat = new THREE.MeshBasicMaterial({ color: locoConfig.headlight.color });
        const headlights = [new THREE.Mesh(headlightGeo, headlightMat), new THREE.Mesh(headlightGeo, headlightMat)];
        const headlightY = locoBody.position.y - locoConfig.height * 0.3;
        const headlightZ = locoConfig.width * 0.3;
        headlights[0].position.set(locoBody.position.x + locoConfig.length / 2, headlightY, headlightZ);
        headlights[1].position.set(locoBody.position.x + locoConfig.length / 2, headlightY, -headlightZ);
        trainGroup.add(...headlights);

        // Locomotive Wheels
        const locoWheels = [new THREE.Mesh(wheelGeo.clone(), wheelMat), new THREE.Mesh(wheelGeo.clone(), wheelMat), new THREE.Mesh(wheelGeo.clone(), wheelMat)];
        locoWheels[0].position.set(locoBody.position.x - locoConfig.length * 0.35, wheelRadius, 0);
        locoWheels[1].position.set(locoBody.position.x + locoConfig.length * 0.35, wheelRadius, 0);
        locoWheels[2].position.set(cab.position.x, wheelRadius, 0);
        trainGroup.add(...locoWheels);

        currentX += locoConfig.length; // No spacing after the locomotive

        // --- 3. Finalize and Center ---
        const totalLength = currentX;

        trainGroup.children.forEach(child => {
            child.position.x -= totalLength / 2;
        });

        trainGroup.traverse(node => {
            if (node.isMesh) {
                node.castShadow = true;
                node.material.clippingPlanes = this.clippingPlanes;
            }
        });

        return { trainGroup, totalLength };
    }
    isTreeAt = (x, z) => this.lanes.some(lane => lane.userData.z === z && lane.userData.obstacles.some(obs => obs.x === x));
    // In CrossyRoad.js

    getLogAt = (worldX, gridZ) => {
        for (const log of this.logs) {
            // We only care about logs on the same Z lane
            if (log.userData.z !== gridZ) continue;

            // --- LOCAL SPACE COLLISION CHECK ---
            // 1. Get the log's local properties
            const logLocalX = log.position.x;
            const logLocalWidth = log.geometry.parameters.width;

            // 2. Calculate the log's local boundaries
            const logMinX = logLocalX - logLocalWidth / 2;
            const logMaxX = logLocalX + logLocalWidth / 2;

            // 3. Check if the player's X position is within these local boundaries
            //    'worldX' is a misnomer here, it's the player's local X within the gameGroup.
            if (worldX >= logMinX && worldX <= logMaxX) {
                return log; // Found it!
            }
        }
        return null; // No log found at this position
    }
    getRandomCarType = () => {
        const rand = Math.random();
        if (rand < 0.15) return 'truck';
        if (rand < 0.60) return 'big';
        return 'small';
    }
    updateSpawners = (time) => {
        for (const lane of this.lanes) {
            if (lane.userData.type === 'road' && time > lane.userData.nextSpawnTime) {
                this.spawnCarOnLane(lane);
                const { min, max } = this.config.obstacle.road.traffic.spawnCooldown;
                lane.userData.nextSpawnTime = time + THREE.MathUtils.randFloat(min, max) * 1000;
            } else if (lane.userData.type === 'water' && time > lane.userData.nextSpawnTime) {
                this.spawnLogOnLane(lane);
                const { min, max } = this.config.obstacle.log.traffic.spawnCooldown;
                lane.userData.nextSpawnTime = time + THREE.MathUtils.randFloat(min, max) * 1000;
            } else if (lane.userData.type === 'train') {
                if (!lane.userData.isWarning && time > lane.userData.nextSpawnTime) {
                    lane.userData.isWarning = true;
                    lane.userData.warningEndTime = time + this.config.train.warningTime * 1000;
                    lane.userData.warningLights.forEach(light => { light.visible = true; gsap.to(light.material, { opacity: 0.2, duration: 0.4, repeat: -1, yoyo: true, ease: "power1.inOut" }); });
                } else if (lane.userData.isWarning && time > lane.userData.warningEndTime) {
                    lane.userData.isWarning = false;
                    lane.userData.warningLights.forEach(light => { gsap.killTweensOf(light.material); light.material.opacity = 1.0; light.visible = false; });
                    this.spawnTrainOnLane(lane);
                    const { min, max } = this.config.train.spawnCooldown;
                    lane.userData.nextSpawnTime = time + THREE.MathUtils.randFloat(min, max) * 1000;
                }
            }
        }
    }
    updateCars = (deltaTime) => {
        const wrapBoundary = (this.config.world.laneWidth / 2) + this.config.obstacle.road.carTypes.truck.length;
        // Iterate backwards to safely remove items from the array while looping
        for (let i = this.cars.length - 1; i >= 0; i--) {
            const car = this.cars[i];
            car.position.x -= car.userData.speed * car.userData.direction * deltaTime;
            if (Math.abs(car.position.x) > wrapBoundary + 5) {
                this.removeObject(car, 'car');
            }
        }
    };

    // --- MODIFIED ---
    updateLogs = (deltaTime) => {
        const logDespawnBoundary = (this.config.world.laneWidth / 2) + this.config.obstacle.log.logType.length.max;
        const playerBoundary = this.config.world.laneWidth / 2;
        const playerHalfWidth = this.config.player.size / 2;

        for (let i = this.logs.length - 1; i >= 0; i--) {
            const log = this.logs[i];
            const deltaX = -log.userData.speed * log.userData.direction * deltaTime;
            log.position.x += deltaX;

            if (this.player.userData.onLog === log && !gsap.isTweening(this.player.position)) {
                this.player.position.x += deltaX;
                if (this.player.position.x - playerHalfWidth > playerBoundary || this.player.position.x + playerHalfWidth < -playerBoundary) {
                    this.onPlayerDeath();
                }
            }

            if (Math.abs(log.position.x) > logDespawnBoundary + 5) {
                if (this.player.userData.onLog === log) {
                    this.onPlayerDeath(); // This will handle game state, but we still need to remove the log
                }
                this.removeObject(log, 'log');
            }
        }
    }
    updateCoins = (deltaTime) => { this.coins.forEach(coin => { coin.rotation.z += this.config.coin.rotationSpeed * deltaTime; }); }
    updateTrains = (deltaTime) => {
        const visuals = this.config.train.visuals;
        const maxPossibleLength = visuals.locomotive.length + visuals.carCount.max * (visuals.car.length + visuals.spacing);
        const wrapBoundary = (this.config.world.laneWidth / 2) + maxPossibleLength;

        for (let i = this.trains.length - 1; i >= 0; i--) {
            const train = this.trains[i];
            train.position.x -= this.config.train.speed * train.userData.direction * deltaTime;
            if (Math.abs(train.position.x) > wrapBoundary + 5) {
                this.removeObject(train, 'train');
            }
        }
    }

    updateActiveLight() {
        if (!this.player) return;

        // 1. Determine which light is active
        const activeLight = this.vrLight.visible ? this.vrLight : this.desktopLight;
        const isVr = this.vrLight.visible;

        // 2. Calculate the player's true WORLD position
        const playerWorldPosition = new THREE.Vector3();
        this.player.getWorldPosition(playerWorldPosition);

        // 3. Update the light's target to the player's WORLD position
        activeLight.target.position.copy(playerWorldPosition);

        // 4. Update the light's position to be offset from the WORLD target
        // The offset vector remains the same, defining the light's angle.
        activeLight.position.copy(activeLight.target.position).add(new THREE.Vector3(-5, 10, 5));

        // 5. THIS IS THE CRITICAL NEW STEP: Dynamically adjust the VR shadow camera
        if (isVr) {
            const gameScale = this.config.vr.scale;

            // Define the desired size of the shadow box in LOCAL game units.
            // Let's make it bigger to ensure full coverage of the lane width.
            const localFrustumSize = 15; // Covers a 16-unit wide area in the game.

            // Scale that size down to get the required WORLD size.
            const worldFrustumSize = localFrustumSize * gameScale;

            // Apply the calculated WORLD size to the shadow camera.
            activeLight.shadow.camera.left = -worldFrustumSize;
            activeLight.shadow.camera.right = worldFrustumSize;
            activeLight.shadow.camera.top = worldFrustumSize;
            activeLight.shadow.camera.bottom = -worldFrustumSize;

            // Also update near/far planes based on world dimensions
            activeLight.shadow.camera.near = 0.1;
            activeLight.shadow.camera.far = 20; // World units

            // You MUST update the projection matrix after changing the frustum.
            activeLight.shadow.camera.updateProjectionMatrix();
        }

        // Finally, update the target's matrix
        activeLight.target.updateMatrixWorld();
    }
    checkCollisions = () => {
        // This guard is still useful to prevent checks while hopping
        if (this.player.userData.onLog || gsap.isTweening(this.player.position)) return;

        const playerX = this.player.position.x;
        const playerZ = this.playerGridPos.z;
        const playerHalfSize = this.config.player.size / 2;

        const playerMinX = playerX - playerHalfSize;
        const playerMaxX = playerX + playerHalfSize;

        // --- Local-space check for CARS ---
        for (const car of this.cars) {
            if (car.userData.z === playerZ) {
                const carLocalX = car.position.x;
                const carLocalWidth = car.userData.length; // We stored this in userData
                const carMinX = carLocalX - carLocalWidth / 2;
                const carMaxX = carLocalX + carLocalWidth / 2;

                // Standard AABB (Axis-Aligned Bounding Box) overlap check
                if (playerMaxX > carMinX && playerMinX < carMaxX) {
                    this.onPlayerDeath();
                    return; // Game over, no need to check further
                }
            }
        }

        // --- Local-space check for TRAINS ---
        for (const train of this.trains) {
            if (train.userData.z === playerZ) {
                const trainLocalX = train.position.x;
                const trainLocalWidth = train.userData.length;
                const trainMinX = trainLocalX - trainLocalWidth / 2;
                const trainMaxX = trainLocalX + trainLocalWidth / 2;

                if (playerMaxX > trainMinX && playerMinX < trainMaxX) {
                    this.onPlayerDeath();
                    return;
                }
            }
        }
    };
    checkCoinCollection = (x, z) => {
        const coinIndex = this.coins.findIndex(c => c.userData.x === x && c.userData.z === z);
        if (coinIndex !== -1) {
            const coin = this.coins[coinIndex];
            this.playSound('coin');
            coin.parent.remove(coin); this.coins.splice(coinIndex, 1);
            this.updateScore(this.score + this.config.coin.value);
            const scorePopup = document.createElement('div');
            scorePopup.textContent = `+${this.config.coin.value}`;
            scorePopup.style.position = 'absolute'; scorePopup.style.color = 'gold'; scorePopup.style.fontSize = '1.5rem'; scorePopup.style.fontWeight = 'bold';
            scorePopup.style.textShadow = '1px 1px 3px black'; scorePopup.style.pointerEvents = 'none';
            scorePopup.style.left = `${window.innerWidth / 2}px`; scorePopup.style.top = `${window.innerHeight / 2}px`;
            scorePopup.style.transform = 'translate(-50%, -50%)';
            document.body.appendChild(scorePopup);
            gsap.to(scorePopup, { y: '-=50', opacity: 0, duration: 1, onComplete: () => scorePopup.remove() });
        }
    }
    generateInitialWorld = () => {
        for (let i = 0; i < this.config.world.laneCount; i++) {
            this.generateLane(i);
        }
    }
    spawnGrassObstaclesForLane = (lane) => {
        const { z } = lane.userData;
        const halfWidth = this.config.world.laneWidth / 2;
        const { min, max } = this.config.obstacle.grass.obstacleCount;
        const numObstacles = THREE.MathUtils.randInt(min, max);

        for (let i = 0; i < numObstacles; i++) {
            const x = THREE.MathUtils.randInt(-halfWidth, halfWidth);
            if (!lane.userData.obstacles.some(obs => obs.x === x)) {
                let obstacle;
                if (Math.random() < 0.65) { obstacle = this.createTree(); } else { obstacle = this.createBush(); }
                obstacle.position.set(x, obstacle.position.y, 0);
                lane.add(obstacle);
                lane.userData.obstacles.push({ x, z });
            }
        }
    }
    spawnCoinForLane = (lane) => {
        if (Math.random() > this.config.coin.spawnChance) return;
        const { z, obstacles } = lane.userData;
        let possiblePositions = [];
        const halfWidth = this.config.world.laneWidth / 2;
        for (let i = -halfWidth; i <= halfWidth; i++) { possiblePositions.push(i); }
        const occupiedPositions = obstacles.map(obs => obs.x);
        const availablePositions = possiblePositions.filter(pos => !occupiedPositions.includes(pos));
        if (availablePositions.length > 0) {
            const x = availablePositions[Math.floor(Math.random() * availablePositions.length)];
            const coin = this.createCoin(); coin.position.set(x, this.config.coin.geometry.radius + this.config.coin.geometry.height, 0); coin.userData = { x, z };
            lane.add(coin); this.coins.push(coin);
        }
    }
    createTrainTrack = () => {
        const trackGroup = new THREE.Group();
        trackGroup.rotateY(Math.PI / 2);
        const railGeo = new THREE.BoxGeometry(0.1, 0.1, this.config.world.laneWidth);
        const railMat = new THREE.MeshStandardMaterial({ color: 0x888888 });
        const rail1 = new THREE.Mesh(railGeo, railMat); rail1.position.x = -0.5;
        const rail2 = new THREE.Mesh(railGeo, railMat); rail2.position.x = 0.5;
        trackGroup.add(rail1, rail2);
        trackGroup.position.y = 0.05;

        const sleeperGeo = new THREE.BoxGeometry(1.5, 0.05, 0.2);
        const sleeperMat = new THREE.MeshStandardMaterial({ color: 0x654321 });
        for (let i = -this.config.world.laneWidth / 2; i < this.config.world.laneWidth / 2; i += 0.5) {
            const sleeper = new THREE.Mesh(sleeperGeo, sleeperMat); sleeper.position.z = i; trackGroup.add(sleeper);
        }
        return trackGroup;
    }
    createWarningLight = () => {
        const light = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.1, 16), new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true }));
        light.rotation.z = Math.PI / 2;
        light.visible = false;
        return light;
    }
    removeObject(object, type) {
        if (!object) return;

        // Remove from the scene graph
        if (object.parent) {
            object.parent.remove(object);
        }

        // Remove from the corresponding tracking array
        switch (type) {
            case 'car':
                this.cars = this.cars.filter(c => c !== object);
                break;
            case 'log':
                this.logs = this.logs.filter(l => l !== object);
                break;
            case 'train':
                this.trains = this.trains.filter(t => t !== object);
                break;
            case 'lane':
                this.lanes = this.lanes.filter(l => l !== object);
                break;
        }

        // Optional: Help the garbage collector by disposing of geometry and materials
        // This is good practice for complex scenes to prevent memory leaks.
        object.traverse(child => {
            if (child.isMesh) {
                child.geometry.dispose();
                // If materials are shared, be careful here. 
                // If they are unique per object, this is safe.
                if (Array.isArray(child.material)) {
                    child.material.forEach(material => material.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }
    // Add this new method to your CrossyRoad class
    setClippingPlanes(scale = 1.0) {
        const halfLaneWidth = this.config.world.laneWidth / 2;
        // Apply the current scale to the plane's constant
        const scaledHalfWidth = halfLaneWidth * scale;

        this.leftClippingPlane = new THREE.Plane(new THREE.Vector3(1, 0, 0), scaledHalfWidth);
        this.rightClippingPlane = new THREE.Plane(new THREE.Vector3(-1, 0, 0), scaledHalfWidth);

        // We also need to transform the planes by the gameGroup's world position
        const gameGroupPosition = new THREE.Vector3();
        this.gameGroup.getWorldPosition(gameGroupPosition);

        this.leftClippingPlane.translate(gameGroupPosition);
        this.rightClippingPlane.translate(gameGroupPosition);

        this.clippingPlanes = [this.leftClippingPlane, this.rightClippingPlane];

        // IMPORTANT: We must update the materials of existing objects
        this.worldGroup.traverse(child => {
            if (child.isMesh && child.material) {
                child.material.clippingPlanes = this.clippingPlanes;
                child.material.needsUpdate = true;
            }
        });
    }
    updateClippingPlanes = () => {
        // Ensure the gameGroup's world matrix is up-to-date before we use it
        this.gameGroup.updateWorldMatrix(true, false);

        // For each world plane:
        // 1. Reset it by copying the local "blueprint" plane.
        // 2. Transform it from local space to world space using the gameGroup's matrix.
        this.worldLeftPlane.copy(this.localLeftPlane).applyMatrix4(this.gameGroup.matrixWorld);
        this.worldRightPlane.copy(this.localRightPlane).applyMatrix4(this.gameGroup.matrixWorld);

        // The PlaneHelpers will update automatically because they hold a reference
        // to the worldLeftPlane and worldRightPlane objects we just updated.
    }

    dispose = () => {
        this.renderer.setAnimationLoop(null);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('keydown', this.handleKeyDown);
        document.removeEventListener('touchstart', this.handleTouchStart);
        document.removeEventListener('touchend', this.handleTouchEnd);

        // Dispose of Three.js objects
        this.scene.traverse(object => {
            if (object.isMesh) {
                if (object.geometry) {
                    object.geometry.dispose();
                }
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            }
        });
        this.renderer.dispose();
    }
}

export default CrossyRoad;